import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Seo } from "@/components/Seo";

const COPY = {
  login: {
    testid: "login-page", heading: "Welcome back",
    sub: "Sign in to see your service history and plan details.",
    submit: "Sign In", submitTestid: "login-submit-btn",
    emailTestid: "login-email-input", passwordTestid: "login-password-input",
  },
  register: {
    testid: "register-page", heading: "Create your account",
    sub: "Your hub for service history, plan status and one-click booking.",
    submit: "Create Account", submitTestid: "register-submit-btn",
    emailTestid: "register-email-input", passwordTestid: "register-password-input",
  },
};

const ForgotPasswordLink = ({ email }) => {
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!email) {
      toast.error("Enter your email above first, then click Forgot password");
      return;
    }
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email exists, a reset link has been sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };
  return (
    <button type="button" onClick={send} disabled={sending} data-testid="forgot-password-btn"
      className="text-xs text-zinc-500 hover:text-brand font-medium mt-3 disabled:opacity-50 transition-colors duration-200">
      Forgot password?
    </button>
  );
};

const AuthSwitch = ({ isLogin }) => (
  <p className="text-sm text-zinc-600 mt-6 text-center">
    {isLogin ? (
      <>New here? <Link to="/register" data-testid="goto-register-link" className="text-brand font-medium">Create an account</Link></>
    ) : (
      <>Already have an account? <Link to="/login" data-testid="goto-login-link" className="text-brand font-medium">Sign in</Link></>
    )}
  </p>
);

const useAuthSubmit = (isLogin) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (form) => {
    setLoading(true);
    setError("");
    const result = isLogin ? await login(form.email, form.password) : await register(form);
    setLoading(false);
    if (result.ok) navigate("/account");
    else setError(result.error);
  };

  return { submit, error, loading };
};

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const copy = COPY[isLogin ? "login" : "register"];
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", website: "" });
  const { submit, error, loading } = useAuthSubmit(isLogin);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid={copy.testid} className="bg-brand-subtle min-h-[70vh] flex items-center">
      <div className="max-w-md w-full mx-auto px-4 py-16">
        <Seo
          title={isLogin ? "Sign In to Your Account | Central Air Depot" : "Create Your Account | Central Air Depot"}
          description={isLogin
            ? "Sign in to view your service history, maintenance plan and requests with Central Air Depot."
            : "Create a free account to track service history, maintenance visits and requests with Central Air Depot."}
        />
        <div className="bg-white border border-zinc-200 p-8 rounded-sm">
          <div className="mb-6">
            <img src="/logo-horizontal.webp" alt="Central Air Depot" className="h-10 w-auto" />
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mt-3">Manage My AC</p>
          </div>
          <h1 className="font-heading font-black text-2xl text-brand-ink">{copy.heading}</h1>
          <p className="text-sm text-zinc-600 mt-1 mb-6">{copy.sub}</p>
          <form onSubmit={(e) => { e.preventDefault(); submit(form); }} className="space-y-4">
            {!isLogin && (
              <>
                <Input data-testid="register-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className="rounded-sm" />
                <Input data-testid="register-phone-input" required placeholder="Phone" type="tel" value={form.phone} onChange={set("phone")} className="rounded-sm" />
                <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 overflow-hidden">
                  <label htmlFor="website-field">Website</label>
                  <input id="website-field" type="text" name="website" tabIndex={-1} autoComplete="off"
                    value={form.website} onChange={set("website")} />
                </div>
              </>
            )}
            <Input data-testid={copy.emailTestid} required placeholder="Email" type="email" value={form.email} onChange={set("email")} className="rounded-sm" />
            <Input data-testid={copy.passwordTestid} required placeholder="Password (8+ characters)" type="password" minLength={8} value={form.password} onChange={set("password")} className="rounded-sm" />
            {error && <p data-testid="auth-error" className="text-sm text-brand-red">{error}</p>}
            <button data-testid={copy.submitTestid} type="submit" disabled={loading}
              className="w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold py-3.5 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {copy.submit}
            </button>
          </form>
          {isLogin && <ForgotPasswordLink email={form.email} />}
          <AuthSwitch isLogin={isLogin} />
        </div>
      </div>
    </div>
  );
}
